import * as React from "react";
import { useState } from "react";

import { Field, withTypes } from "react-final-form";

import axios from "axios";

import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DeleteIcon from "@material-ui/icons/Delete";

import { Notification, useTranslate, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    padding: theme.spacing(2),
  },
  card: {
    minWidth: 300,
    marginTop: "6em",
  },
  form: {
    padding: "0 1em 1em 1em",
    width: "100%",
    maxWidth: 600,
  },
  input: {
    marginTop: "1em",
  },
  actions: {
    padding: "0 1em 1em 1em",
    width: "100%",
    maxWidth: 600,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    width: "100%",
    maxWidth: 1400,
  },
  table: {
    minWidth: 650,
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.grey[500],
  },
  blockedChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

interface FormValues {
  email?: string;
}

interface DeviceSignature {
  id: number;
  device_fingerprint: string;
  platform: string;
  device_id: string;
  device_uuid: string;
  first_user_id: number;
  user_count: number;
  is_blocked: boolean;
  block_reason: string;
  blocked_at: string;
  blocked_by: number;
  createdAt: string;
  updatedAt: string;
}

const { Form } = withTypes<FormValues>();

const ManageDeviceSignatures = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [deviceToAction, setDeviceToAction] = useState<DeviceSignature | null>(null);
  const [actionType, setActionType] = useState<'block' | 'unblock' | 'delete'>('block');
  const [processing, setProcessing] = useState(false);
  const [currentEmail, setCurrentEmail] = useState<string>("");
  const [blockReason, setBlockReason] = useState<string>("");
  const translate = useTranslate();
  const classes = useStyles();
  const notify = useNotify();

  const handleSearch = (inputData: any) => {
    const { email } = inputData;
    setLoading(true);
    setCurrentEmail(email);

    const config = getConfigGet({ email: email });

    axios
      .get(ENDPOINTS.MANAGE_DEVICE_SIGNATURES, config)
      .then((res: any) => {
        const responseData = res.data?.data || res.data;

        if (Array.isArray(responseData) && responseData.length > 0) {
          setTableData(responseData);
          const rowCount = responseData.length - 1;
          if (rowCount === 0) {
            notify("No device signatures found for this user", "info", "", undefined, 5000);
          } else {
            notify(`Found ${rowCount} device signature(s)`, "info", "", undefined, 5000);
          }
        } else {
          setTableData([]);
          notify("No device signatures found for this user", "info", "", undefined, 5000);
        }

        setLoading(false);
      })
      .catch((error) => {
        notify(
          error?.response?.data?.message || `Failed to fetch device signatures`,
          "error",
          "",
          undefined,
          50000
        );
        setTableData([]);
        setLoading(false);
      });
  };

  const handleActionClick = (device: DeviceSignature, action: 'block' | 'unblock' | 'delete') => {
    setDeviceToAction(device);
    setActionType(action);
    setBlockReason("");
    setActionDialogOpen(true);
  };

  const handleActionConfirm = () => {
    if (!deviceToAction || !currentEmail) return;

    setProcessing(true);
    const headers = getConfigPost();

    const payload = {
      email: currentEmail,
      deviceId: deviceToAction.id,
      action: actionType,
      reason: actionType === 'block' ? blockReason : undefined,
    };

    axios
      .post(ENDPOINTS.MANAGE_DEVICE_SIGNATURES, payload, { headers })
      .then((res: any) => {
        const messages = {
          block: "Device signature blocked successfully (referral rewards disabled)",
          unblock: "Device signature unblocked successfully",
          delete: "Device signature deleted successfully"
        };
        notify(messages[actionType], "info", "", undefined, 5000);
        setActionDialogOpen(false);
        setDeviceToAction(null);
        setProcessing(false);
        setBlockReason("");
        handleSearch({ email: currentEmail });
      })
      .catch((error) => {
        notify(
          error?.response?.data?.message || `Failed to ${actionType} device signature`,
          "error",
          "",
          undefined,
          50000
        );
        setProcessing(false);
      });
  };

  const handleActionCancel = () => {
    setActionDialogOpen(false);
    setDeviceToAction(null);
    setBlockReason("");
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.email) {
      errors.email = translate("ra.validation.required");
    }
    return errors;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const truncateFingerprint = (fp: string) => {
    if (!fp) return 'N/A';
    return `${fp.substring(0, 8)}...${fp.substring(fp.length - 8)}`;
  };

  return (
    <div className={classes.main}>
      <Form
        onSubmit={handleSearch}
        validate={validate}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate>
            <div className={classes.form}>
              <div className={classes.input}>
                <Field
                  autoFocus
                  name="email"
                  // @ts-ignore
                  component={renderInput}
                  label={translate("pos.email")}
                  disabled={loading}
                />
              </div>
            </div>
            <CardActions className={classes.actions}>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading}
                fullWidth
              >
                {loading && <CircularProgress size={25} thickness={2} />}
                Search Device Signatures
              </Button>
            </CardActions>
          </form>
        )}
      />

      {tableData.length > 0 && (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table} aria-label="device signatures table">
            <TableHead>
              <TableRow>
                {tableData[0].map((header: string, index: number) => (
                  <TableCell key={index}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.slice(1).map((row: any[], rowIndex: number) => {
                const device = row[row.length - 1] as DeviceSignature;

                return (
                  <TableRow key={rowIndex}>
                    {/* ID */}
                    <TableCell>{row[0]}</TableCell>
                    {/* Platform */}
                    <TableCell>
                      <Chip
                        label={row[1]}
                        size="small"
                        color={row[1] === 'ios' ? 'primary' : 'secondary'}
                        className={classes.statusChip}
                      />
                    </TableCell>
                    {/* Fingerprint */}
                    <TableCell>
                      <Tooltip title={row[2] || ''}>
                        <span>{truncateFingerprint(row[2])}</span>
                      </Tooltip>
                    </TableCell>
                    {/* First User ID */}
                    <TableCell>{row[3]}</TableCell>
                    {/* User Count */}
                    <TableCell>
                      <Chip
                        label={row[4]}
                        size="small"
                        color={row[4] > 1 ? 'secondary' : 'default'}
                        className={classes.statusChip}
                      />
                    </TableCell>
                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={row[5]}
                        size="small"
                        className={row[5] === 'Blocked' ? classes.blockedChip : classes.activeChip}
                      />
                    </TableCell>
                    {/* Block Reason */}
                    <TableCell>{row[6] || 'N/A'}</TableCell>
                    {/* Created At */}
                    <TableCell>{row[7]}</TableCell>
                    {/* Actions */}
                    <TableCell>
                      {device.is_blocked ? (
                        <Tooltip title="Unblock Device (re-enable referral rewards)">
                          <IconButton
                            aria-label="unblock"
                            color="primary"
                            onClick={() => handleActionClick(device, 'unblock')}
                            disabled={processing}
                            size="small"
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Block Device (disable referral rewards)">
                          <IconButton
                            aria-label="block"
                            color="secondary"
                            onClick={() => handleActionClick(device, 'block')}
                            disabled={processing}
                            size="small"
                          >
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Signature (remove device record)">
                        <IconButton
                          aria-label="delete"
                          style={{ color: '#d32f2f' }}
                          onClick={() => handleActionClick(device, 'delete')}
                          disabled={processing}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={actionDialogOpen}
        onClose={handleActionCancel}
        aria-labelledby="action-dialog-title"
        aria-describedby="action-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="action-dialog-title">
          {actionType === 'block' && 'Block Device Signature'}
          {actionType === 'unblock' && 'Unblock Device Signature'}
          {actionType === 'delete' && 'Delete Device Signature'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="action-dialog-description" component="div">
            {actionType === 'block' && (
              <>
                <p><strong>This will disable referral rewards for this device.</strong></p>
                <p>The user can still login normally.</p>
                <br />
                <strong>Platform:</strong> {deviceToAction?.platform || "N/A"}
                <br />
                <strong>User Count:</strong> {deviceToAction?.user_count || 0}
                <br />
                <strong>First User ID:</strong> {deviceToAction?.first_user_id || "N/A"}
                <br /><br />
                <TextField
                  label="Block Reason"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  placeholder="Enter reason for blocking this device..."
                />
              </>
            )}
            {actionType === 'unblock' && (
              <>
                <p>Are you sure you want to unblock this device signature?</p>
                <p>This will re-enable referral rewards for this device.</p>
                <br />
                <strong>Platform:</strong> {deviceToAction?.platform || "N/A"}
                <br />
                <strong>Block Reason:</strong> {deviceToAction?.block_reason || "N/A"}
                <br />
                <strong>Blocked At:</strong> {deviceToAction?.blocked_at ? formatDate(deviceToAction.blocked_at) : "N/A"}
              </>
            )}
            {actionType === 'delete' && (
              <>
                <p style={{ color: '#d32f2f' }}><strong>Warning: This will permanently delete the device signature.</strong></p>
                <p>On next login, the device will be treated as a new device.</p>
                <br />
                <strong>Platform:</strong> {deviceToAction?.platform || "N/A"}
                <br />
                <strong>User Count:</strong> {deviceToAction?.user_count || 0}
                <br />
                <strong>First User ID:</strong> {deviceToAction?.first_user_id || "N/A"}
                <br />
                <strong>Fingerprint:</strong> {deviceToAction?.device_fingerprint?.substring(0, 16)}...
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionCancel} color="primary" disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleActionConfirm}
            color="secondary"
            variant="contained"
            disabled={processing || (actionType === 'block' && !blockReason.trim())}
          >
            {processing ? <CircularProgress size={20} /> : actionType === 'block' ? 'Block' : actionType === 'unblock' ? 'Unblock' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default ManageDeviceSignatures;
